import { useEffect } from "react";
import { usePageMeta } from "@/components/pepmuse/usePageMeta";
import { MousePointerClick, ShieldCheck, Rocket, Layers, RefreshCw, LifeBuoy, TrendingUp, Lock } from "lucide-react";
import { SubpageHero } from "@/components/pepmuse/SubpageHero";
import { PricingPanel } from "@/components/pepmuse/PricingPanel";
import { BrowserArt } from "@/components/pepmuse/BrowserArt";
import { ClosingCTA } from "@/components/pepmuse/ClosingCTA";
import { Reveal, Eyebrow } from "@/components/pepmuse/Reveal";

const FEATURES = [
  {
    icon: MousePointerClick,
    title: "Designed to convert",
    body: "Every page guides visitors toward checkout — clean layouts, clear calls to action, and as few steps as possible.",
    items: ["Considered, modern design", "Product pages that answer questions", "Streamlined checkout", "Trust signals throughout"],
  },
  {
    icon: ShieldCheck,
    title: "Structured for compliance",
    body: "Research-use disclaimers, legal pages, and documentation patterns are part of the build — never bolted on.",
    items: ["Research-use product copy", "Required legal pages", "Disclaimer placement", "Documentation framework"],
  },
  {
    icon: Rocket,
    title: "Live in weeks",
    body: "A focused, scheduled process takes your store from kickoff to launch in about two to three weeks.",
    items: ["Fast, scheduled build", "Commerce pre-configured", "Payment gateway setup", "Foundational SEO"],
  },
  {
    icon: Layers,
    title: "Fully turnkey",
    body: "Hosting, security, fulfillment connection, and the technical plumbing are handled for you — and editing content stays simple.",
    items: ["Hosting arranged", "SSL certificate included", "3PL fulfillment connection", "Easy content editing"],
  },
];

const SUPPORT = [
  { icon: RefreshCw, title: "Compliance updates", body: "We track research-use requirements and help keep your store current as they evolve." },
  { icon: LifeBuoy, title: "Technical support", body: "Real help on email when something breaks, confuses, or needs a second pair of eyes — included for your first three months as you launch." },
  { icon: TrendingUp, title: "Growth check-ins", body: "Periodic conversations about optimization, new features, and what to build next." },
  { icon: Lock, title: "Security & maintenance", body: "Updates, patches, and performance monitoring so the store simply stays up." },
];

export default function WebsiteCreation({ onBuild }) {
  usePageMeta(
    "Website Creation — PepMuse",
    "A custom mobile-first storefront for research peptide brands — catalog, checkout, research-use compliance structure, and 3PL fulfillment connection. Live in weeks."
  );

  return (
    <>
      <SubpageHero
        testId="web-hero"
        eyebrow="Website creation"
        title="A store that feels like your brand."
        body="A custom online storefront designed for research peptide brands — mobile-first, conversion-aware, structured for research-use compliance, and ready to plug into wholesale supply and 3PL fulfillment. Live in weeks."
        onBuild={onBuild}
        secondary={{ label: "See what you get", target: "website-included" }}
        art={<BrowserArt />}
      />

      <section id="website-included" data-testid="web-included" className="scroll-mt-24 py-20 sm:py-24" tabIndex={-1}>
        <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
          <Reveal>
            <Eyebrow index="01" testId="web-included-eyebrow">What you get</Eyebrow>
            <h2 className="mt-4 max-w-xl font-display text-3xl font-extrabold tracking-tight text-paper sm:text-4xl">
              A storefront with the details done.
            </h2>
            <p className="mt-4 max-w-md text-base text-paper/60">
              Design, structure, and setup — everything a high-converting, compliant store needs.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {FEATURES.map((feature, i) => (
              <Reveal key={feature.title} delay={i * 0.06}>
                <div data-testid={`web-feature-card-${i + 1}`} className="h-full rounded-3xl border border-white/[0.08] bg-card p-7 sm:p-8">
                  <div className="flex items-start gap-5">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-lilac-bright to-lilac-deep text-ink shadow-[0_10px_20px_-8px_rgba(154,131,216,0.6)]">
                      <feature.icon className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
                    </span>
                    <div>
                      <h3 className="font-display text-xl font-extrabold text-paper">{feature.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-paper/60">{feature.body}</p>
                    </div>
                  </div>
                  <ul className="mt-6 grid gap-2.5 border-t border-white/[0.07] pt-5 sm:grid-cols-2">
                    {feature.items.map((item) => (
                      <li key={item} className="flex items-center gap-2.5 text-sm text-paper/70">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-lilac" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="web-pricing-section" className="py-8 sm:py-12">
        <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
          <PricingPanel
            testId="web-pricing"
            packageKey="website-creation"
            eyebrow="Special launch pricing"
            title="Website creation"
            price="$4,997"
            regular="$6,500"
            note="One-time design & development. Year-one hosting included (renews at about $297/yr), plus 3 months of technical support."
            bullets={[
              "Custom design, catalog, and checkout",
              "Compliance-ready structure from day one",
              "Live in about two to three weeks",
            ]}
            onBuild={onBuild}
          />
        </div>
      </section>

      <section data-testid="web-support" className="py-20 sm:py-24">
        <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
          <Reveal>
            <Eyebrow index="02" testId="web-support-eyebrow">After launch</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-paper sm:text-4xl">
              We build it. Then we stay around.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SUPPORT.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.06}>
                <div data-testid={`web-support-card-${i + 1}`} className="h-full rounded-3xl border border-white/[0.08] bg-card p-7">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-lilac/15 text-lilac">
                    <item.icon className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-display text-base font-extrabold text-paper">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-paper/60">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ClosingCTA onBuild={() => onBuild()} />
    </>
  );
}
